import { tutorialTaskDone } from '../../../../shared/src/tutorial/events'
import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Layers, Pencil, Trash2 } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button, SearchInput, Card, Modal, Input, EmptyState, Badge } from '@/components/ui'
import { useAuthStore } from '@/store/authStore'
import { groupsApi, type DesktopSchedulePreview } from '@/api/groupsApi'
import { athletesApi } from '@/api/athletesApi'
import { GroupRoster } from './GroupRoster'
import { toast } from '@/components/ui/Toast'
import type { Group, CreateGroupRequest, UpdateGroupRequest } from '@/types'

const DAY_NAMES_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const DAY_NAMES_FULL = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота']

interface ScheduleSlot {
  day_of_week: number
  start_time: string
  end_time: string
  location?: string
}

function formatSchedule(scheduleJson: string | undefined): string {
  if (!scheduleJson) return '-'
  try {
    const slots: ScheduleSlot[] = JSON.parse(scheduleJson)
    if (!slots.length) return '-'
    return slots
      .map(s => `${DAY_NAMES_SHORT[s.day_of_week]} ${s.start_time}-${s.end_time}${s.location ? ` (${s.location})` : ''}`)
      .join(', ')
  } catch {
    return scheduleJson // fallback to raw string
  }
}

function parseScheduleSlots(scheduleJson: string | undefined): ScheduleSlot[] {
  if (!scheduleJson) return []
  try {
    const slots: ScheduleSlot[] = JSON.parse(scheduleJson)
    return Array.isArray(slots) ? slots : []
  } catch {
    return []
  }
}

export const GroupsPage: React.FC = () => {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId)
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [form, setForm] = useState<CreateGroupRequest>({ name: '' })
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState<UpdateGroupRequest>({})
  const [editScheduleSlots, setEditScheduleSlots] = useState<ScheduleSlot[]>([])
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [scheduleFrom, setScheduleFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })
  const [schedulePreview, setSchedulePreview] = useState<DesktopSchedulePreview | null>(null)
  const [previewBusy, setPreviewBusy] = useState(false)

  const openDetail = (group: Group) => {
    setSelectedGroup(group)
    setSchedulePreview(null)
    setEditMode(false)
    setConfirmDelete(false)
    setEditForm({
      name: group.name,
      description: group.description,
      max_size: group.max_size,
    })
    setEditScheduleSlots(parseScheduleSlots(group.schedule))
  }

  const closeDetail = () => {
    if (previewBusy || updateMutation.isPending) return
    if (editMode && !window.confirm('Закрыть группу и отменить несохранённые правки?')) return
    setSelectedGroup(null)
    setEditMode(false)
    setConfirmDelete(false)
  }

  const { data: groups = [], isLoading, isError: groupsError, refetch: refetchGroups } = useQuery({
    queryKey: ['groups', currentAssistantId],
    queryFn: () => groupsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  })

  // Athletes in selected group
  const { data: allAthletes = [], isLoading: rosterLoading, isError: rosterError } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId && !!selectedGroup,
  })

  const createMutation = useMutation({
    mutationFn: (req: CreateGroupRequest) => groupsApi.create(currentAssistantId!, req),
    onSuccess: (group) => {
      tutorialTaskDone('group', group.id)
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      setShowCreate(false)
      setForm({ name: '' })
      toast.success('Группа создана')
    },
    onError: (err: any) => toast.error(err?.error || 'Ошибка создания'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateGroupRequest }) => {
      if (schedulePreview?.changed) {
        await groupsApi.replaceSchedule(id, editScheduleSlots, schedulePreview)
        if (editScheduleSlots.length) tutorialTaskDone('schedule')
      }
      return groupsApi.update(currentAssistantId!, id, { ...req, clear_description: !req.description })
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      setSelectedGroup(updated)
      setEditMode(false)
      toast.success('Группа обновлена')
    },
    onError: (err: any) => { setSchedulePreview(null); void queryClient.invalidateQueries({ queryKey: ['groups', currentAssistantId] }); toast.error(`${err?.error || 'Не удалось сохранить'}. Если расписание уже применилось, оно остаётся в силе; проверьте группу перед повтором.`) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => groupsApi.delete(currentAssistantId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['athletes'] })
      closeDetail()
      toast.success('Группа удалена')
    },
    onError: (err: any) => toast.error(err?.error || 'Ошибка удаления'),
  })

  const filtered = groups.filter((g: Group) =>
    g.name.toLowerCase().includes(search.toLowerCase())
  )

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedGroup) return
    if (editScheduleSlots.some(s => !s.start_time || !s.end_time || s.end_time <= s.start_time)) { toast.error('Конец занятия должен быть позже начала'); return }
    if (new Set(editScheduleSlots.map(s => `${s.day_of_week}:${s.start_time}`)).size !== editScheduleSlots.length) { toast.error('Занятия с одинаковым началом повторяются'); return }
    const changed = JSON.stringify(editScheduleSlots) !== JSON.stringify(parseScheduleSlots(selectedGroup.schedule))
    if (changed && !schedulePreview) {
      setPreviewBusy(true)
      try { setSchedulePreview(await groupsApi.previewSchedule(selectedGroup.id, editScheduleSlots, scheduleFrom)) }
      catch (err) { toast.error((err as { error?: string }).error || 'Не удалось получить предпросмотр') }
      finally { setPreviewBusy(false) }
      return
    }
    updateMutation.mutate({ id: selectedGroup.id, req: editForm })
  }

  const addScheduleSlot = () => {
    setSchedulePreview(null)
    setEditScheduleSlots([...editScheduleSlots, { day_of_week: 1, start_time: '18:00', end_time: '19:30' }])
  }

  const updateScheduleSlot = (index: number, field: keyof ScheduleSlot, value: string | number) => {
    setSchedulePreview(null)
    const updated = [...editScheduleSlots]
    updated[index] = { ...updated[index], [field]: value }
    setEditScheduleSlots(updated)
  }

  const removeScheduleSlot = (index: number) => {
    setSchedulePreview(null)
    setEditScheduleSlots(editScheduleSlots.filter((_, i) => i !== index))
  }

  if (!currentAssistantId) {
    return (
      <>
        <Header title="Группы" />
        <EmptyState icon={Layers} title="Выберите направление" />
      </>
    )
  }

  return (
    <>
      <Header
        title="Группы"
        subtitle={`${groups.length} групп`}
        actions={<Button data-tutorial="new-group" size="sm" onClick={() => setShowCreate(true)}><Plus size={16} className="mr-1.5" />Создать</Button>}
      />

      <div className="pc-page">
        <div className="mb-4">
          <SearchInput value={search} onChange={setSearch} placeholder="Поиск группы..." className="max-w-sm" />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-[var(--accent)] border-t-transparent rounded-full" /></div>
        ) : groupsError ? <div className="pc-panel p-8" role="alert">Не удалось загрузить группы. <Button variant="secondary" onClick={() => void refetchGroups()}>Повторить</Button></div> : filtered.length === 0 && !search ? (
          <EmptyState icon={Layers} title="Нет групп" description="Создайте первую группу" actionLabel="Создать" onAction={() => setShowCreate(true)} />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 md:grid-cols-2 gap-4">
            {filtered.map((g: Group) => (
              <Card key={g.id} className="cursor-pointer hover:opacity-95" onClick={() => openDetail(g)}>
                <div data-tutorial="group-card" className="flex items-center justify-between mb-2">
                  <h3 className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>{g.name}</h3>
                  <Badge>{g.athletes_count ?? 0} чел.</Badge>
                </div>
                {g.description && (
                  <p className="text-[13px] mb-2" style={{ color: 'var(--text-secondary)' }}>{g.description}</p>
                )}
                <p className="text-[12px]" style={{ color: 'var(--text-hint)' }}>{formatSchedule(g.schedule)}</p>
                {g.monthly_price != null && g.monthly_price > 0 && (
                  <p className="text-[12px] mt-1" style={{ color: 'var(--text-hint)' }}>{(g.monthly_price / 100).toLocaleString('ru-RU')} ₽/мес</p>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Новая группа">
        <form data-tutorial="group-form" onSubmit={(e) => { e.preventDefault(); createMutation.mutate(form) }} className="flex flex-col gap-4">
          <Input label="Название *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          <Input label="Описание" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value || undefined })} />
<p className="pc-muted text-sm">После создания откройте группу и задайте дни и время в редакторе расписания.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="secondary" type="button" onClick={() => setShowCreate(false)}>Отмена</Button>
            <Button type="submit" loading={createMutation.isPending}>Создать</Button>
          </div>
        </form>
      </Modal>

      {/* Detail / Edit Modal */}
      <Modal open={!!selectedGroup} onClose={closeDetail} title={editMode ? 'Редактирование группы' : (selectedGroup?.name ?? 'Группа')} width="xl">
        {/* Read-only view */}
        {selectedGroup && !editMode && (
          <div className="flex flex-col gap-5">
            {/* Group info */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[18px] font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedGroup.name}</div>
                {selectedGroup.description && (
                  <div className="text-[14px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>{selectedGroup.description}</div>
                )}
              </div>
              <Badge>{selectedGroup.athletes_count ?? 0} / {selectedGroup.max_size || '\u221E'}</Badge>
            </div>

            {/* Details grid */}
            <SectionTitle>Информация</SectionTitle>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <DetailRow label="Спортсменов" value={String(selectedGroup.athletes_count ?? 0)} />
              <DetailRow label="Макс. размер" value={selectedGroup.max_size && selectedGroup.max_size > 0 ? String(selectedGroup.max_size) : 'Без ограничений'} />
              {selectedGroup.monthly_price != null && selectedGroup.monthly_price > 0 && (
                <DetailRow label="Стоимость" value={`${(selectedGroup.monthly_price / 100).toLocaleString('ru-RU')} ₽/мес`} />
              )}
            </div>

            {/* Schedule display */}
            <SectionTitle>Расписание</SectionTitle>
            {(() => {
              const slots = parseScheduleSlots(selectedGroup.schedule)
              if (slots.length === 0) {
                return <div className="text-[14px]" style={{ color: 'var(--text-hint)' }}>Расписание не задано</div>
              }
              return (
                <div className="space-y-2">
                  {slots.map((slot, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <span className="text-[13px] font-semibold min-w-[100px]" style={{ color: 'var(--text-primary)' }}>
                        {DAY_NAMES_FULL[slot.day_of_week] ?? `День ${slot.day_of_week}`}
                      </span>
                      <span className="text-[13px]" style={{ color: 'var(--accent)' }}>
                        {slot.start_time} - {slot.end_time}
                      </span>
                      {slot.location && (
                        <span className="text-[12px]" style={{ color: 'var(--text-hint)' }}>
                          {slot.location}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )
            })()}

            <GroupRoster key={selectedGroup.id} group={selectedGroup} athletes={allAthletes} groups={groups} assistantId={currentAssistantId} loading={rosterLoading} error={rosterError} />

            {/* Metadata */}
            <div className="flex gap-6 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              <div className="text-[12px]" style={{ color: 'var(--text-hint)' }}>
                Создана: {new Date(selectedGroup.created_at).toLocaleDateString('ru-RU')}
              </div>
              <div className="text-[12px]" style={{ color: 'var(--text-hint)' }}>
                Обновлена: {new Date(selectedGroup.updated_at).toLocaleDateString('ru-RU')}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-1">
              <div>
                {!confirmDelete ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmDelete(true)}
                    style={{ color: 'var(--danger)' }}
                  >
                    <Trash2 size={14} className="mr-1.5" />Удалить
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-[13px]" style={{ color: 'var(--danger)' }}>Удалить группу?</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMutation.mutate(selectedGroup.id)}
                      loading={deleteMutation.isPending}
                      style={{ color: 'var(--danger)' }}
                    >
                      Да, удалить
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                      Отмена
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={closeDetail}>Закрыть</Button>
                <Button onClick={() => setEditMode(true)}>
                  <Pencil size={16} className="mr-1.5" />Редактировать
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit form */}
        {selectedGroup && editMode && (
          <form onSubmit={handleSubmitEdit}><fieldset disabled={previewBusy || updateMutation.isPending} className="flex flex-col gap-4 min-w-0">
            <SectionTitle>Основные данные</SectionTitle>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Input
                label="Название *"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                autoFocus
              />

            </div>
            <Input
              label="Описание"
              value={editForm.description || ''}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value || undefined })}
            />

            <Input label="Расписание действует с" type="date" required value={scheduleFrom} onChange={e => { setScheduleFrom(e.target.value); setSchedulePreview(null) }} />
            {/* Schedule builder */}
            <span data-tutorial="schedule">Дни и время занятий</span>
            <SectionTitle>Расписание</SectionTitle>
            <div className="space-y-2">
              {editScheduleSlots.map((slot, index) => (
                <div key={index} className="flex items-end gap-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[12px] font-medium" style={{ color: 'var(--text-hint)' }}>День</label>
                    <select
                      value={slot.day_of_week}
                      onChange={(e) => updateScheduleSlot(index, 'day_of_week', parseInt(e.target.value))}
                      className="h-9 px-3 rounded-lg text-[13px] outline-none min-w-[130px]"
                      style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    >
                      {DAY_NAMES_FULL.map((name, i) => (
                        <option key={i} value={i}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[12px] font-medium" style={{ color: 'var(--text-hint)' }}>Начало</label>
                    <input
                      type="time"
                      value={slot.start_time}
                      onChange={(e) => updateScheduleSlot(index, 'start_time', e.target.value)}
                      className="h-9 px-3 rounded-lg text-[13px] outline-none"
                      style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[12px] font-medium" style={{ color: 'var(--text-hint)' }}>Конец</label>
                    <input
                      type="time"
                      value={slot.end_time}
                      onChange={(e) => updateScheduleSlot(index, 'end_time', e.target.value)}
                      className="h-9 px-3 rounded-lg text-[13px] outline-none"
                      style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 flex-1">
                    <label className="text-[12px] font-medium" style={{ color: 'var(--text-hint)' }}>Место</label>
                    <input
                      type="text"
                      value={slot.location || ''}
                      onChange={(e) => updateScheduleSlot(index, 'location', e.target.value)}
                      placeholder="Зал, площадка..."
                      className="h-9 px-3 rounded-lg text-[13px] outline-none"
                      style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeScheduleSlot(index)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg hover:opacity-70 shrink-0"
                    style={{ color: 'var(--danger)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <Button type="button" variant="secondary" size="sm" onClick={addScheduleSlot}>
                <Plus size={14} className="mr-1" />Добавить слот
              </Button>
            </div>

            {schedulePreview && <div className="pc-panel p-4 text-sm space-y-2" role="status">
              <strong>Предпросмотр с {schedulePreview.from}</strong>
              <p>Изменений: {schedulePreview.items?.length || 0}. Занятий с отметками сохраняется: {schedulePreview.kept_marked}. Запланированных версий заменяется: {schedulePreview.replace_version_ids?.length || 0}.</p>
              {!!schedulePreview.kept_marked && <p>Отмеченные занятия останутся в журнале отдельными занятиями.</p>}
              {schedulePreview.suggested_from && <Button type="button" variant="secondary" size="sm" onClick={() => { setScheduleFrom(schedulePreview.suggested_from!); setSchedulePreview(null) }}>Начать с {schedulePreview.suggested_from}</Button>}
              {schedulePreview.items?.map((item, i) => <p key={i}>{item.date}: {item.change}, отметок {item.marks}, {item.outcome === 'deleted' ? 'будет удалено' : 'сохраняется'}</p>)}
              <p>Следующее нажатие подтвердит показанные изменения.</p>
            </div>}
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="secondary" type="button" onClick={() => setEditMode(false)}>Отмена</Button>
              <Button type="submit" loading={updateMutation.isPending || previewBusy}>{schedulePreview ? 'Подтвердить и сохранить' : 'Проверить и сохранить'}</Button>
            </div>
          </fieldset></form>
        )}
      </Modal>
    </>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[13px] font-semibold uppercase tracking-wider pt-1" style={{ color: 'var(--text-hint)' }}>
      {children}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex gap-2">
      <span className="text-[13px] min-w-[140px]" style={{ color: 'var(--text-hint)' }}>{label}</span>
      <span className="text-[14px]" style={{ color: 'var(--text-primary)' }}>{value || '-'}</span>
    </div>
  )
}
